import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, MapPin, Mail, Flame, MessageCircle } from "lucide-react";
import Marquee from "react-fast-marquee";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [info, setInfo] = useState({});

  useEffect(() => {
    api.get("/content/contact_info").then((r) => setInfo(r.data || {})).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setError("");
    try {
      await api.post("/newsletter", { email });
      setStatus("success");
      setEmail("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed.");
      setStatus("error");
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/10 bg-[#050505]"
    >
      <div className="border-y border-white/10 py-4 bg-[#0a0a0a]">
        <Marquee gradient={false} speed={45} pauseOnHover>
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="font-display uppercase text-2xl md:text-3xl tracking-tight text-zinc-700 mx-8 flex items-center gap-8"
            >
              Unstoppable Generation
              <Flame className="w-5 h-5 text-[#ff5c00]" />
              RCCG Dubai
              <span className="text-[#ff5c00]">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-4">
          <img
            src="/brand/ugyc-logo-white.webp"
            alt="UGYC Dubai"
            data-testid="footer-crest"
            className="h-44 md:h-52 w-auto object-contain"
          />
          <p className="text-zinc-400 text-sm leading-relaxed max-w-md">
            The Unstoppable Generation Young Adults & Youth Church — a parish of
            The Redeemed Christian Church of God (RCCG), raising a fearless
            generation of Jesus-followers in the heart of Dubai.
          </p>

          <div className="flex items-center gap-3 pt-2 border-t border-white/5 mt-2">
            <div className="bg-white rounded-sm p-1.5 mt-3">
              <img
                src="/rccg-logo.png"
                alt="RCCG"
                data-testid="rccg-logo-footer"
                className="w-9 h-9 object-contain"
              />
            </div>
            <div className="mt-3">
              <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500">Affiliated with</p>
              <a
                href="https://www.rccg.org/"
                target="_blank"
                rel="noreferrer"
                className="text-sm text-zinc-200 hover:text-[#ff5c00] transition-colors"
              >
                The Redeemed Christian Church of God
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <a
              href="https://www.instagram.com/rccg_ugycdubai"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              className="w-10 h-10 rounded-sm border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] flex items-center justify-center text-zinc-300 transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://youtube.com/@rccg_ugycdubai"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-youtube"
              className="w-10 h-10 rounded-sm border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] flex items-center justify-center text-zinc-300 transition-colors"
              aria-label="YouTube"
            >
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3 space-y-3">
          <p className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-500">
            Explore
          </p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">About</Link></li>
            <li><Link to="/beliefs" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Beliefs</Link></li>
            <li><Link to="/events" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Events</Link></li>
            <li><Link to="/sermons" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Sermons</Link></li>
            <li><Link to="/ministries" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Ministries</Link></li>
            <li><Link to="/sunday-school" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Sunday School</Link></li>
            <li><Link to="/gallery" className="text-zinc-300 hover:text-[#ff5c00] transition-colors">Gallery</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4 space-y-4">
          <p className="font-mono-accent text-xs tracking-[0.2em] uppercase text-zinc-500">
            Stay In The Loop
          </p>
          <form onSubmit={handleSubscribe} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-[#121217] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="newsletter-submit-button"
                className="bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-5 rounded-sm transition-colors"
              >
                {status === "loading" ? "..." : "Join"}
              </button>
            </div>
            {status === "success" && (
              <p data-testid="newsletter-success" className="text-xs text-[#22c55e]">
                You're in. See you Sunday.
              </p>
            )}
            {status === "error" && (
              <p data-testid="newsletter-error" className="text-xs text-red-400">{error}</p>
            )}
          </form>

          <div className="space-y-2 pt-2 text-sm text-zinc-400">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#ff5c00]" />
              <span data-testid="footer-address">
                {info.address_line1 || "Speedex Building, Abubaker Al Siddique Metro, Dubai, UAE"}
                {info.address_line2 ? `, ${info.address_line2}` : ""}
                {info.address_line3 ? `, ${info.address_line3}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#ff5c00]" />
              <a href={`mailto:${info.email || "ugyouthchurch@gmail.com"}`} data-testid="footer-email" className="hover:text-white">
                {info.email || "ugyouthchurch@gmail.com"}
              </a>
            </div>
            {info.whatsapp && (
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#ff5c00]" />
                <a
                  href={`https://wa.me/${info.whatsapp.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="hover:text-white"
                >
                  {info.whatsapp}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 py-6 px-6 md:px-12 max-w-7xl mx-auto flex flex-col md:flex-row gap-2 items-center justify-between text-xs text-zinc-500 font-mono-accent uppercase tracking-[0.15em]">
        <span>© {new Date().getFullYear()} UGYC Dubai · A parish of RCCG</span>
        <Link to="/login" data-testid="footer-admin-link" className="hover:text-[#ff5c00]">Admin</Link>
      </div>
    </footer>
  );
}
